import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Navigate, matchPath, useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import { MdOutlineManageAccounts } from 'react-icons/md';
import { getSupportSession, isSupportSession, subscribeSupportSession } from '@/auth/supportSession';
import { openSupportSession } from '@/auth/supportSessionClient';
import { auth } from '@/auth';
import { AuthActionCreators } from '@/store/actionCreators/authCreators';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { useDocumentTitlePrefix } from '@hooks/useDocumentTitlePrefix';
import SupportFrame from '@atoms/SupportFrame';
import { publicRoutes } from '@/router';
import type { AppDispatch } from '@/store';
import './SupportSessionGate.scss';

/** Приставка заголовка вкладки «от имени»: не перепутать со своей вкладкой. */
const supportTitlePrefix = (userName: string) => `[Поддержка] ${userName} — `;

/**
 * Вход во вкладке «от имени»: открывает сессию по билету, держит её срок и закрывает вкладку для работы, когда
 * сессия кончилась (сервер ответил 401 — сеанс закрыт или истёк). Пока вход открыт — янтарная рамка окна
 * и приставка заголовка (PLAN.md §7.3). В обычной вкладке пропускает содержимое как есть.
 */
export default function SupportSessionGate({ children }: { children: ReactNode }) {
    const session = useSyncExternalStore(subscribeSupportSession, getSupportSession);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState('');
    const dispatch = useDispatch<AppDispatch>();
    const authenticated = useTypedSelector((state) => state.auth.isAuth);
    const location = useLocation();

    useEffect(() => {
        if (!isSupportSession()) return;
        let disposed = false;
        void openSupportSession()
            .then(async () => {
                const current = getSupportSession();
                if (current && !disposed) await dispatch(AuthActionCreators.getCurrentUser(current.accessToken));
            })
            .catch((reason: unknown) => {
                if (!disposed) setError(reason instanceof Error ? reason.message : 'Не удалось открыть кабинет.');
            })
            .finally(() => {
                if (!disposed) setReady(true);
            });
        return () => {
            disposed = true;
        };
    }, [dispatch]);

    useEffect(() => {
        if (!session) return;
        const expire = () => {
            if (Date.parse(session.expiresAt) <= Date.now()) auth.clearSession();
        };
        const timer = window.setTimeout(expire, Math.max(0, Date.parse(session.expiresAt) - Date.now()));
        window.addEventListener('focus', expire);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('focus', expire);
        };
    }, [session]);

    const open = isSupportSession() && ready && !!session && authenticated && !error;
    useDocumentTitlePrefix(open && session ? supportTitlePrefix(session.userName) : null);

    if (!isSupportSession()) return children;
    if (!open)
        return (
            <main className="support-session-gate" aria-live="polite">
                <section className="support-session-gate__card">
                    <MdOutlineManageAccounts size={36} aria-hidden />
                    <h1>{!ready ? 'Открываем кабинет пользователя…' : 'Вход от имени завершён'}</h1>
                    <p>
                        {!ready
                            ? 'Подготавливаем отдельную сессию для этой вкладки.'
                            : error ||
                              'Сеанс закрыт, истёк или завершён администратором. Чтобы войти снова, откройте кабинет из карточки сотрудника в админке. Эту вкладку можно закрыть.'}
                    </p>
                </section>
            </main>
        );
    if (
        /\/support\/session\/?$/.test(location.pathname) ||
        publicRoutes.some((route) => matchPath(route.path, location.pathname))
    )
        return <Navigate to="/" replace />;
    return (
        <>
            {children}
            <SupportFrame tone="control" />
        </>
    );
}
